// src/services/api/orders.api.ts
import { apiRequest } from "./client";
import {
    OrdersPageResponse,
    ListOrdersFilters,
    OrderItem,
    AddPaymentPayload,
    UpdateOrderPayload,
    OrderPaymentItem,
    ApplyOrderDiscountPayload,
} from "../types/orders.types";

// Параметры фильтра списка заказов — общие для списка и выгрузки в Excel.
const appendOrdersFilterParams = (params: URLSearchParams, filters: ListOrdersFilters) => {
    if (filters.order_number?.trim()) {
        params.set("order_number", filters.order_number.trim());
    }

    if (filters.customer_name?.trim()) {
        params.set("customer_name", filters.customer_name.trim());
    }

    if (filters.customer_email?.trim()) {
        params.set("customer_email", filters.customer_email.trim());
    }

    if (filters.customer_phone?.trim()) {
        params.set("customer_phone", filters.customer_phone.trim());
    }

    if (filters.invoice_number?.trim()) {
        params.set("invoice_number", filters.invoice_number.trim());
    }

    if (filters.comment?.trim()) {
        params.set("comment", filters.comment.trim());
    }

    if (typeof filters.is_paid === "boolean") {
        params.set("is_paid", String(filters.is_paid));
    }

    if (typeof filters.is_deleted === "boolean") {
        params.set("is_deleted", String(filters.is_deleted));
    }

    if (typeof filters.need_delivery === "boolean") {
        params.set("need_delivery", String(filters.need_delivery));
    }

    if (filters.payment_type) {
        params.set("payment_type", filters.payment_type);
    }

    if (filters.site_source) {
        params.set("site_source", filters.site_source);
    }

    if (filters.partner) {
        params.set("partner", filters.partner);
    }

    if (filters.date_from) {
        params.set("date_from", filters.date_from);
    }

    if (filters.date_to) {
        params.set("date_to", filters.date_to);
    }

    if (typeof filters.is_agent === "boolean") {
        params.set("is_agent", String(filters.is_agent));
    }

    if (filters.customer_id) {
        params.set("customer_id", filters.customer_id);
    }

    if (filters.tour_id) {
        params.set("tour_id", filters.tour_id);
    }

    if (filters.tour_city_id) {
        params.set("tour_city_id", filters.tour_city_id);
    }
};

export const getOrdersRequest = async (
    page: number,
    pageSize: number,
    filters: ListOrdersFilters,
): Promise<OrdersPageResponse> => {
    const params = new URLSearchParams();

    params.set("page", String(page));
    params.set("page_size", String(pageSize));

    appendOrdersFilterParams(params, filters);

    return apiRequest<OrdersPageResponse>(`/orders?${params.toString()}`, {
        method: "GET",
        auth: true,
    });
};

export const getOrderRequest = async (orderId: string): Promise<OrderItem> => {
    return apiRequest<OrderItem>(`/orders/${orderId}`, {
        method: "GET",
        auth: true,
    });
};

export const addOrderPaymentRequest = async (
    orderId: string,
    payload: AddPaymentPayload,
): Promise<OrderPaymentItem> => {
    return apiRequest<OrderPaymentItem>(`/orders/${orderId}/payments`, {
        method: "POST",
        auth: true,
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });
};

export const updateOrderRequest = async (
    orderId: string,
    payload: UpdateOrderPayload,
): Promise<OrderItem> => {
    return apiRequest<OrderItem>(`/orders/${orderId}`, {
        method: "PATCH",
        auth: true,
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });
};

// POST /orders/{order_id}/apply-discount — админ применяет скидку к неоплаченному заказу,
// распределяя её равно ("amount") или процентно ("percent") между всеми билетами.
export const applyOrderDiscountRequest = async (
    orderId: string,
    payload: ApplyOrderDiscountPayload,
): Promise<OrderItem> => {
    return apiRequest<OrderItem>(`/orders/${orderId}/apply-discount`, {
        method: "POST",
        auth: true,
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });
};

// DELETE /orders/{order_id}/seats/{hall_seat_price_id} — админ удаляет один билет из ещё неоплаченного заказа;
// сумма/скидка заказа пересчитываются на бэкенде, место возвращается в продажу.
export const removeOrderSeatRequest = async (
    orderId: string,
    hallSeatPriceId: string,
): Promise<OrderItem> => {
    return apiRequest<OrderItem>(`/orders/${orderId}/seats/${hallSeatPriceId}`, {
        method: "DELETE",
        auth: true,
    });
};

// ===== Пересадка заказа на другие места =====
export type RelocationSeatItem = {
    hall_seat_price_id: string;
    tour_city_id: string;
    tour_name: string;
    city_name: string;
    hall_name: string;
    start_at: string | null;
    sector_name: string;
    row_name: string;
    seat_name: string;
    base_price: number;
    final_price: number;
    // только у «куда»: >0 — скидка к новому месту, <0 — доплата к билету
    adjustment?: number;
    discount_name?: string | null;
    barcode?: string | null;
    is_same_seat?: boolean;
    // Билет стоячей зоны (танцпол / фан-зона): вместо ряда/места — название зоны.
    is_standing?: boolean;
    standing_name?: string | null;
};

// Билеты стоячей зоны при пересадке: сколько билетов нужно в зоне (элемент схемы) города тура.
export type RelocationStandingItem = {
    hall_scheme_element_id: string;
    tour_city_id: string;
    count: number;
};

export type RelocationPreview = {
    order_id: string;
    order_number: string;
    invoice_number: string | null;
    tickets_count: number;
    from_items: RelocationSeatItem[];
    to_items: RelocationSeatItem[];
    subtotal_before: number;
    subtotal_after: number;
    discount_before: number;
    discount_after: number;
    tickets_total: number;
    order_total: number;
    is_paid: boolean;
    is_invite: boolean;
    notification: "tickets" | "invoice" | null;
    // Пересадка на концерт партнёра: artist_production / nashe_de, иначе null.
    target_partner?: "artist_production" | "nashe_de" | null;
};

export type RelocationResult = {
    ok: boolean;
    order: OrderItem;
    notification: "tickets" | "invoice" | null;
    notification_sent_to: string | null;
    warning: string | null;
};

export const previewRelocateOrderRequest = async (
    orderId: string,
    hallSeatPriceIds: string[],
    standing: RelocationStandingItem[] = [],
): Promise<RelocationPreview> =>
    apiRequest<RelocationPreview>(`/orders/${orderId}/relocate/preview`, {
        method: "POST",
        auth: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hall_seat_price_ids: hallSeatPriceIds, standing }),
    });

export const relocateOrderRequest = async (
    orderId: string,
    hallSeatPriceIds: string[],
    standing: RelocationStandingItem[] = [],
): Promise<RelocationResult> =>
    apiRequest<RelocationResult>(`/orders/${orderId}/relocate`, {
        method: "POST",
        auth: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hall_seat_price_ids: hallSeatPriceIds, standing }),
    });

// ===== Восстановление удалённого заказа =====

export type RestoreSeatInfo = {
    hall_seat_price_id: string;
    tour_name: string;
    city_name: string;
    start_at: string | null;
    sector_name: string;
    row_name: string;
    seat_name: string;
    is_standing: boolean;
    standing_name: string;
    available: boolean;
    // missing | sold | in_cart | admin_reserved | unavailable
    reason: string | null;
    reason_text: string | null;
};

export type RestorePreview = {
    order_id: string;
    order_number: string;
    can_restore: boolean;
    // Причина, по которой заказ нельзя восстановить целиком (партнёрский, возврат,
    // мероприятие прошло, одноразовый промокод уже использован и т.п.).
    block_reason: { code: string; message: string } | null;
    seats_taken: number;
    seats: RestoreSeatInfo[];
    // Заказ удалён до появления снимка мест — список взят из всей истории заказа.
    legacy_snapshot: boolean;
    is_paid: boolean;
    total_price: string | null;
};

export const getRestoreOrderPreviewRequest = (orderId: string) =>
    apiRequest<RestorePreview>(`/orders/${orderId}/restore/preview`, {
        method: "GET",
        auth: true,
    });

export const restoreOrderRequest = (orderId: string) =>
    apiRequest<{ ok: boolean; order: OrderItem }>(`/orders/${orderId}/restore`, {
        method: "POST",
        auth: true,
    });

// ===== Send actions =====

export interface SendResult {
    ok: boolean;
    message: string;
    order_id: string;
    order_number: string;
}

export const sendOrderInvoiceRequest = (orderId: string) =>
    apiRequest<SendResult>(`/orders/${orderId}/send-invoice`, {
        method: "POST",
        auth: true,
    });

export const sendOrderTicketsRequest = (orderId: string) =>
    apiRequest<SendResult>(`/orders/${orderId}/send-tickets`, {
        method: "POST",
        auth: true,
    });

export const sendOrderLieferscheinRequest = (orderId: string) =>
    apiRequest<SendResult>(`/orders/${orderId}/send-lieferschein`, {
        method: "POST",
        auth: true,
    });

// Письмо с произвольной темой и текстом на email покупателя заказа.
export interface SendOrderEmailResult {
    ok: boolean;
    message: string;
    order_id: string;
    order_number: string;
    sent_to: string;
}

export const sendOrderEmailRequest = (orderId: string, subject: string, message: string) =>
    apiRequest<SendOrderEmailResult>(`/orders/${orderId}/send-email`, {
        method: "POST",
        auth: true,
        body: JSON.stringify({ subject, message }),
        headers: {
            "Content-Type": "application/json",
        },
    });

// ===== Tickets list + single send =====

export interface TicketItem {
    ticket_no: number;
    hall_seat_price_id?: string | null;
    barcode?: string | null;
    order_number?: string | null;
    tour_name?: string | null;
    city_name?: string | null;
    hall_name?: string | null;
    event_date?: string | null;
    sector_name?: string | null;
    row_name?: string | number | null;
    seat_name?: string | number | null;
    price?: string | null;
    price_label?: string | null;
}

export interface OrderTicketsResponse {
    order_id: string;
    order_number: string;
    customer_email?: string | null;
    tickets: TicketItem[];
}

export const getOrderTicketsRequest = (orderId: string) =>
    apiRequest<OrderTicketsResponse>(`/orders/${orderId}/tickets`, {
        method: "GET",
        auth: true,
    });

export const sendOrderSingleTicketRequest = (orderId: string, ticketNo: number) =>
    apiRequest<SendResult & { ticket_no: number }>(
        `/orders/${orderId}/tickets/${ticketNo}/send-email`,
        {
            method: "POST",
            auth: true,
        },
    );


// POST /orders/admin-create — создание заказа админом из модалки цен зала
export interface AdminOrderNewCustomerPayload {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    phone?: string;
}

export interface AdminOrderCreatePayload {
    customer_id?: string;
    new_customer?: AdminOrderNewCustomerPayload;
    hall_seat_price_ids: string[];
    is_invite: boolean;
    payment_type?: "cash" | "rechnung" | "stripe";
    comment?: string;
    // Доставка — точно как на обычном сайте.
    need_delivery?: boolean;
    country?: string;
    city?: string;
    zip_code?: string;
    address?: string;
    // Скидки по отдельным билетам ("rabatt"): hall_seat_price_id -> discount_id.
    seat_discounts?: Record<string, string>;
}

export const adminCreateOrderRequest = async (
    payload: AdminOrderCreatePayload,
): Promise<OrderItem> => {
    return apiRequest<OrderItem>("/orders/admin-create", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
    });
};

// ---- Партнёрские заказы (Artist Production API) ----
// Полный цикл: резерв → подтверждение оплаты, либо явная отмена резерва.

export interface PartnerOrderReservePayload {
    customer_id?: string;
    new_customer?: AdminOrderNewCustomerPayload;
    hall_seat_price_ids: string[];
    payment_type?: "cash" | "rechnung" | "stripe" | "cod";
    comment?: string;
    need_delivery?: boolean;
    country?: string;
    city?: string;
    zip_code?: string;
    address?: string;
}

export const partnerReserveOrderRequest = async (
    payload: PartnerOrderReservePayload,
): Promise<OrderItem> => {
    return apiRequest<OrderItem>("/partner-orders/reserve", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
    });
};

export const partnerConfirmOrderRequest = async (orderId: string): Promise<OrderItem> => {
    return apiRequest<OrderItem>(`/partner-orders/${orderId}/confirm`, {
        method: "POST",
    });
};

export const partnerCancelOrderRequest = async (orderId: string): Promise<OrderItem> => {
    return apiRequest<OrderItem>(`/partner-orders/${orderId}/cancel`, {
        method: "POST",
    });
};


// ---------- PDF downloads (invoice / tickets / lieferschein) ----------

const downloadOrderPdf = async (
    orderId: string,
    kind: "invoice-pdf" | "tickets-pdf" | "lieferschein-pdf" | "delivery-docx",
    fallbackName: string,
    extension: string = "pdf",
): Promise<{ blob: Blob; filename: string }> => {
    const { API_BASE_URL } = await import("../config/env");
    const { getAccessToken } = await import("../storage/token.storage");

    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/orders/${orderId}/${kind}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            if (data?.detail) detail = String(data.detail);
        } catch {
            // ignore
        }
        throw new Error(detail);
    }

    const blob = await response.blob();

    let filename = `${fallbackName}-${orderId}.${extension}`;
    const cd = response.headers.get("content-disposition");
    if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?\"?([^\";]+)\"?/i);
        if (m && m[1]) filename = decodeURIComponent(m[1]);
    }

    return { blob, filename };
};

export const downloadOrderInvoicePdfRequest = (orderId: string) =>
    downloadOrderPdf(orderId, "invoice-pdf", "invoice");

export const downloadOrderTicketsPdfRequest = (orderId: string) =>
    downloadOrderPdf(orderId, "tickets-pdf", "tickets");

export const downloadOrderLieferscheinPdfRequest = (orderId: string) =>
    downloadOrderPdf(orderId, "lieferschein-pdf", "lieferschein");

// Адресная наклейка «Доставка» — редактируемый Word-файл (.docx), адрес из заказа.
export const downloadOrderDeliveryDocxRequest = (orderId: string) =>
    downloadOrderPdf(orderId, "delivery-docx", "Lieferung", "docx");

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Excel со всеми заказами по текущему фильтру панели, сгруппированными по клиентам
 * (вкладки «Клиенты», «Распространители», «API»). GET /orders/export/grouped.
 */
export const downloadOrdersGroupedExcelRequest = async (
    filters: ListOrdersFilters,
): Promise<{ blob: Blob; filename: string }> => {
    const { API_BASE_URL } = await import("../config/env");
    const { getAccessToken } = await import("../storage/token.storage");

    const params = new URLSearchParams();
    appendOrdersFilterParams(params, filters);

    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}/orders/export/grouped?${params.toString()}`, {
        method: "GET",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
        let detail = `HTTP ${response.status}`;
        try {
            const data = await response.json();
            if (data?.detail) detail = String(data.detail);
        } catch {
            // ignore
        }
        throw new Error(detail);
    }

    const blob = await response.blob();
    let filename = "orders.xlsx";
    const cd = response.headers.get("content-disposition");
    if (cd) {
        // Сначала пробуем UTF-8 имя (filename*=), затем обычное.
        const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const plain = cd.match(/filename="?([^";]+)"?/i);
        if (utf8 && utf8[1]) filename = decodeURIComponent(utf8[1]);
        else if (plain && plain[1]) filename = plain[1];
    }
    return { blob, filename };
};
